import { PrintsSlider } from '@theme/prints-slider';
import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Recently viewed products. The viewed product ids live in localStorage, so the
 * cards can't be rendered server-side on first paint. On connect we read the ids
 * and ask the Section Rendering API to render this same section against the
 * search URL (`?q=id:123 OR id:456`), which populates the `search` object and
 * lets the Liquid render the framed print cards inside `#recently-viewed-cards`.
 * The shared PrintsSlider base handles the slider once the cards are swapped in.
 */
class RecentlyViewedSection extends PrintsSlider {
  async load() {
    const ids = RecentlyViewed.getProducts();
    const sectionId = this.dataset.sectionId;

    // The section starts hidden to avoid a flash of placeholder cards. With no
    // history we stay hidden for real visitors, but reveal the placeholders in
    // the theme editor so the merchant can still style the section.
    if (ids.length === 0) {
      if (this.designMode) this.revealPlaceholders();
      return;
    }

    if (!sectionId) return;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((/** @type {string} */ id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    let markup;
    try {
      markup = await sectionRenderer.getSectionHTML(sectionId, false, url);
    } catch {
      return;
    }
    if (!markup) return;

    const cards = new DOMParser().parseFromString(markup, 'text/html').getElementById('recently-viewed-cards');

    if (!cards || cards.children.length === 0) {
      if (this.designMode) this.revealPlaceholders();
      return;
    }

    this.showCards(cards.children);
  }
}

if (!customElements.get('recently-viewed-section')) {
  customElements.define('recently-viewed-section', RecentlyViewedSection);
}
